import { Col, Image, Row } from "antd";
import { groupBy } from "lodash";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import apiFactory from "../api";
import { useGlobalDownloader } from "../context/global/DownloadProvider";
import { useMenuContext } from "../context/MenuContext";
import { ActionMenu, FileItem } from "../pages/conversation/chat/PanelView";

const limit = 40;
const ImagePanel = ({
  resourceList,
  jumpToMessage,
  setSelectedMedia,
  setMediaTypes,
  setIsFetchData,
  setVisible,
  isLoading,
  loadMoreDesc,
  fetchMediaResource,
  isLastGroup,
}) => {
  const downloader = useGlobalDownloader();
  const lastObserver = useRef(null);

  const lastElementRef = useCallback(
    (node) => {
      if (!node) {
        lastObserver.current?.disconnect();
        return;
      }
      if (!isLastGroup || !loadMoreDesc) return;

      const scrollContainer = node.closest("[data-media-scroll-container]");

      lastObserver.current?.disconnect();
      lastObserver.current = new IntersectionObserver(
        async ([entry]) => {
          if (
            !entry.isIntersecting ||
            isLoading ||
            !loadMoreDesc
          )
            return;
          lastObserver.current?.disconnect();
          await fetchMediaResource("desc");
        },
        {
          root: scrollContainer,
          rootMargin: "24px 0px",
          threshold: 0,
        },
      );
      lastObserver.current.observe(node);
    },
    [
      isLoading,
      loadMoreDesc,
      fetchMediaResource,
      isLastGroup,
    ],
  );

  useEffect(() => {
    return () => {
      lastObserver.current?.disconnect();
    };
  }, []);
  const openModal = async (imageData) => {
    const result = await apiFactory.conversationApi.detailResource({
      mainPath: imageData?.mainPath,
    });

    setSelectedMedia(result?.data);
    setMediaTypes(["IMAGE"]);
    setIsFetchData(true);
    setVisible(true);
  };

  if (!resourceList?.length) return null;

  return (
    <div>
      <Row>
        {resourceList?.map((img, index) => (
          <Col
            span={8}
            className="p-[3px] h-[70px] parent-action-menu"
            key={img?.mainPath}
            ref={
              isLastGroup && index === resourceList.length - 1
                ? lastElementRef
                : null
            }
          >
            <Image
              src={img?.thumbnailPreviewPath || img?.mainPath}
              className="rounded cursor-pointer object-cover border border-gray-300"
              preview={false}
              width="100%"
              height="100%"
              onClick={() => openModal(img)}
            />
            <ActionMenu
              file={img}
              downloader={downloader}
              jumpToMessage={jumpToMessage}
            />
          </Col>
        ))}
      </Row>
    </div>
  );
};

const FilePanel = ({ resourceList, jumpToMessage }) => {
  if (!resourceList?.length) return null;

  return (
    <div>
      {resourceList?.map((file, i) => (
        <FileItem
          file={file}
          jumpToMessage={jumpToMessage}
          key={file?.mainPath}
        />
      ))}
    </div>
  );
};

const useMediaResource = () => {
  const { selectedConversation } = useMenuContext();
  const [isFetchData, setIsFetchData] = useState(false);
  const [visible, setVisible] = useState(false);
  const [mediaTypes, setMediaTypes] = useState([]);

  /* firstMedia is the newest media */
  const [firstMedia, setFirstMedia] = useState(null);
  const [lastMedia, setLastMedia] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);

  const [mediaResourceDates, setMediaResourceDates] = useState([]);
  const [collapseDates, setCollapseDates] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [loadMoreDesc, setLoadMoreDesc] = useState(true);
  const [loadMoreAsc, setLoadMoreAsc] = useState(true);

  const calculateSequence = (direction) => {
    if (!direction) return selectedMedia?.sequence;

    if (direction === "asc") return firstMedia?.sequence;
    if (direction === "desc") return lastMedia?.sequence;
  };

  const updateThresholdMedia = (mediaResourceList, direction) => {
    if (!mediaResourceList?.length) {
      if (direction === "asc") setLoadMoreAsc(false);
      if (direction === "desc") setLoadMoreDesc(false);
      return;
    }

    const firstItem = mediaResourceList[0];
    const lastItem = mediaResourceList[mediaResourceList.length - 1];

    if (!direction) {
      setFirstMedia(firstItem);
      setLastMedia(lastItem);
      return;
    }

    if (direction === "asc") {
      setFirstMedia(firstItem);
      if (mediaResourceList.length < limit) setLoadMoreAsc(false);
    }

    if (direction === "desc") {
      setLastMedia(lastItem);
      if (mediaResourceList.length < limit) setLoadMoreDesc(false);
    }
  };

  const handleClose = () => {
    setIsFetchData(false);
    setVisible(false);
    setSelectedMedia(null);
    setMediaResourceDates([]);
    setCollapseDates([]);
  };

  const fetchMediaResource = async (direction) => {
    if (!selectedConversation?.conversationId || !isFetchData) return;
    try {
      setIsLoading(true);

      const result = await apiFactory.conversationApi.getResourceList({
        conversationId: selectedConversation.conversationId,
        sequence: calculateSequence(direction),
        limit: limit,
        direction: direction,
        mediaTypes: mediaTypes,
      });

      const newData = Object.entries(groupBy(result?.data || [], "date")).map(
        ([date, files]) => ({
          key: date,
          label: date,
          files,
        }),
      );

      setMediaResourceDates((prev) => {
        const mediaResourceMap = new Map(
          (prev || []).map((item) => [item.key, item]),
        );

        newData.forEach((item) => {
          const previousItem = mediaResourceMap.get(item.key);
          const files =
            direction === "asc"
              ? [...item.files, ...(previousItem?.files || [])]
              : [...(previousItem?.files || []), ...item.files];

          mediaResourceMap.set(item.key, {
            ...item,
            files,
          });
        });

        setCollapseDates((prev) => [
          ...new Set([...(prev || []), ...newData.map(({ key }) => key)]),
        ]);

        const newKeys = new Set(newData.map((item) => item.key));
        const existingGroups = (prev || []).filter(
          (item) => !newKeys.has(item.key),
        );
        const mergedGroups = newData.map((item) => mediaResourceMap.get(item.key));
        const mergedDates =
          direction === "asc"
            ? [...mergedGroups, ...existingGroups]
            : [...existingGroups, ...mergedGroups];

        return mergedDates;
      });

      updateThresholdMedia(result?.data, direction);
    } catch (err) {
      console.error("Error load resources:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const mediaResourceItems = mediaResourceDates.map((item, index) => ({
    ...item,
    children: mediaTypes?.includes("IMAGE") ? (
      <ImagePanel
        resourceList={item.files}
        setSelectedMedia={setSelectedMedia}
        setMediaTypes={setMediaTypes}
        setIsFetchData={setIsFetchData}
        setVisible={setVisible}
        isLoading={isLoading}
        loadMoreDesc={loadMoreDesc}
        fetchMediaResource={fetchMediaResource}
        isLastGroup={index === mediaResourceDates.length - 1}
      />
    ) : (
      <FilePanel resourceList={item.files} />
    ),
  }));

  useEffect(() => {
    fetchMediaResource(null);
  }, [isFetchData]);

  return {
    visible,
    setVisible,
    isFetchData,
    setIsFetchData,
    mediaTypes,
    setMediaTypes,
    isLoading,
    setIsLoading,
    loadMoreAsc,
    loadMoreDesc,

    mediaResourceDates: mediaResourceItems,
    collapseDates,
    setCollapseDates,
    fetchMediaResource,

    setSelectedMedia,
    selectedMedia,
    handleClose,
  };
};

export { useMediaResource };
